// oxlint-disable react/only-export-components -- compound component (Object.assign) pattern
import { Heading } from "@/components/ui/Heading";
import { Text } from "@/components/ui/Text";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { Loader2, SaveIcon } from "lucide-react";
import { createContext, type ComponentProps, use } from "react";

type SettingsSectionTone = "default" | "danger";
type SettingsSectionContextValue = {
  tone: SettingsSectionTone;
};
type SettingsSectionSlotProps = {
  children?: React.ReactNode;
  className?: string;
};

const DEFAULT_SETTINGS_SECTION_CONTEXT: SettingsSectionContextValue = {
  tone: "default",
};
const DANGER_SETTINGS_SECTION_CONTEXT: SettingsSectionContextValue = {
  tone: "danger",
};
const SettingsSectionContext = createContext(DEFAULT_SETTINGS_SECTION_CONTEXT);

function SettingsSectionRoot({
  children,
  id,
}: {
  children: React.ReactNode;
  id?: string;
}) {
  return (
    <SettingsSectionContext.Provider value={DEFAULT_SETTINGS_SECTION_CONTEXT}>
      <section id={id} className="space-y-3 scroll-mt-4">
        {children}
      </section>
    </SettingsSectionContext.Provider>
  );
}

function DangerSettingsSectionRoot({
  children,
  id,
}: {
  children: React.ReactNode;
  id?: string;
}) {
  return (
    <SettingsSectionContext.Provider value={DANGER_SETTINGS_SECTION_CONTEXT}>
      <section id={id} className="space-y-3 scroll-mt-4">
        {children}
      </section>
    </SettingsSectionContext.Provider>
  );
}

function SettingsSectionHeader({
  children,
  className,
}: SettingsSectionSlotProps) {
  return <div className={cn("space-y-1", className)}>{children}</div>;
}

function SettingsSectionTitle({
  children,
  className,
}: SettingsSectionSlotProps) {
  const { tone } = use(SettingsSectionContext);

  return (
    <Heading
      variant="h4"
      className={cn(
        "normal-case",
        tone === "danger" && "text-destructive",
        className,
      )}
    >
      {children}
    </Heading>
  );
}

function SettingsSectionDescription({
  children,
  className,
}: SettingsSectionSlotProps) {
  return (
    <Text muted small className={cn("max-w-3xl", className)}>
      {children}
    </Text>
  );
}

function SettingsSectionPanel({
  children,
  className,
}: SettingsSectionSlotProps) {
  const { tone } = use(SettingsSectionContext);

  return (
    <div
      className={cn(
        "overflow-hidden border bg-card",
        tone === "danger" && "border-destructive/30",
        className,
      )}
    >
      {children}
    </div>
  );
}

function SettingsSectionBody({
  children,
  className,
}: SettingsSectionSlotProps) {
  return <div className={cn("space-y-4 p-6", className)}>{children}</div>;
}

function SettingsSectionFooter({
  children,
  className,
}: SettingsSectionSlotProps) {
  const { tone } = use(SettingsSectionContext);

  return (
    <div
      className={cn(
        "flex min-h-[56px] items-center justify-between gap-4 border-t px-6 py-3",
        tone === "danger" ? "bg-destructive/5" : "bg-muted/30",
        className,
      )}
    >
      {children}
    </div>
  );
}

function SettingsSectionFooterHint({
  children,
  className,
}: SettingsSectionSlotProps) {
  return (
    <Text muted small className={className}>
      {children}
    </Text>
  );
}

function SettingsSectionFooterActions({
  children,
  className,
}: SettingsSectionSlotProps) {
  return (
    // `ml-auto` rather than the footer's `justify-between`: with no hint beside
    // them the actions are the only child, and justify-between parks a lone
    // child at the start. They belong on the right whether or not anything is
    // said next to them.
    <div className={cn("ml-auto flex shrink-0 items-center gap-2", className)}>
      {children}
    </div>
  );
}

const settingsSectionSlots = {
  Header: SettingsSectionHeader,
  Title: SettingsSectionTitle,
  Description: SettingsSectionDescription,
  Panel: SettingsSectionPanel,
  Body: SettingsSectionBody,
  Footer: SettingsSectionFooter,
  FooterHint: SettingsSectionFooterHint,
  FooterActions: SettingsSectionFooterActions,
};

export const SettingsSection = Object.assign(
  SettingsSectionRoot,
  settingsSectionSlots,
);
export const DangerSettingsSection = Object.assign(
  DangerSettingsSectionRoot,
  settingsSectionSlots,
);

type FooterSaveButtonProps = Omit<ComponentProps<typeof Button>, "children"> & {
  pending: boolean;
};

export function FooterSaveButton({
  pending,
  ...buttonProps
}: FooterSaveButtonProps): JSX.Element {
  return (
    <Button variant="primary" size="md" {...buttonProps}>
      <Button.LeftIcon>
        {pending ? (
          <Loader2 aria-hidden="true" className="size-4 animate-spin" />
        ) : (
          <SaveIcon aria-hidden="true" className="size-4" />
        )}
      </Button.LeftIcon>
      <Button.Text>{pending ? "Saving" : "Save"}</Button.Text>
    </Button>
  );
}
